<template>
  <CBox class="network-ai-chat">
    <CFlex class="network-ai-chat__panel">
      <CBox class="network-ai-chat__scroll-area">
        <CBox
          ref="messagesContainer"
          class="network-ai-chat__body"
          :class="{ 'network-ai-chat__body--conversation': haveMessages || isLoading }"
          @scroll.passive="handleMessagesScroll"
        >
          <CFlex v-if="!haveMessages && !isLoading" class="network-ai-chat__home">
            <CFlex class="network-ai-chat__logo">
              <CBox as="img" class="network-ai-chat__logo-image" :src="omintLogoSrc" alt="Omint" />
            </CFlex>

            <OmtTypography as="h1-bold" class="network-ai-chat__title">Como posso ajudar?</OmtTypography>
            <OmtTypography as="p1" class="network-ai-chat__subtitle">
              Fale o profissional ou estabelecimento que deseja encontrar.
            </OmtTypography>

            <CFlex class="network-ai-chat__composer network-ai-chat__composer--home">
              <OmtTextarea
                ref="textarea"
                class="network-ai-chat__textarea"
                :initialValue="observation"
                placeholder="Ex.: Quero um cardiologista próximo de mim"
                @change="handleChange"
                @keydown.native="handleTextareaKeydown"
              />

              <OmtButton
                v-if="!hasDraft && !isRecording"
                class="network-ai-chat__icon-button"
                type="button"
                title="Usar voz"
                @click="startRecording"
              >
                <OmtIcons name="microphone" color="#0047A1" />
              </OmtButton>

              <OmtButton
                v-else-if="isRecording"
                class="network-ai-chat__icon-button"
                type="button"
                title="Parar gravação"
                @click="stopRecording"
              >
                <OmtIcons name="xmark" color="#0047A1" />
              </OmtButton>

              <OmtButton
                v-else
                class="network-ai-chat__icon-button"
                type="button"
                title="Enviar"
                :isDisabled="isLoading"
                @click="handleSend"
              >
                <OmtIcons name="paper-plane-top" color="#0047A1" />
              </OmtButton>
            </CFlex>

            <CBox class="network-ai-chat__suggestions">
              <OmtTypography as="p2" class="network-ai-chat__suggestions-title">Sugestões de busca</OmtTypography>
              <CFlex class="network-ai-chat__suggestions-list">
                <CBox
                  v-for="suggestion in suggestions"
                  :key="suggestion.title"
                  as="button"
                  type="button"
                  class="network-ai-chat__suggestion"
                  @click="applySuggestion(suggestion.text)"
                >
                  <CBox as="span" class="network-ai-chat__suggestion-icon">
                    <OmtIcons :name="suggestion.icon" color="#0047A1" size="sm" />
                  </CBox>
                  <span>{{ suggestion.title }}</span>
                </CBox>
              </CFlex>
            </CBox>
          </CFlex>

          <CBox v-else class="network-ai-chat__messages">
            <CFlex
              v-for="(message, index) in messages"
              :key="message.id"
              class="network-ai-chat__message-row"
              :class="{
                'network-ai-chat__message-row--user': message.from === 'user',
                'network-ai-chat__message-row--providers': message.providers
              }"
            >
              <CFlex v-if="message.from !== 'user' && shouldShowBotAvatar(index)" class="network-ai-chat__avatar">
                <CBox as="img" class="network-ai-chat__logo-image" :src="omintLogoSrc" alt="" />
              </CFlex>
              <CBox v-else-if="message.from !== 'user'" class="network-ai-chat__avatar-spacer" aria-hidden="true" />

              <CBox v-if="message.providers" class="network-ai-chat__providers">
                <CBox v-for="provider in message.providers" :key="provider.id" class="network-ai-chat__provider-card">
                  <CBox class="network-ai-chat__provider-content">
                    <CFlex class="network-ai-chat__provider-main">
                      <CFlex class="network-ai-chat__provider-icon">
                        <OmtIcons name="user-doctor-hair" color="#0047A1" size="2x" />
                      </CFlex>

                      <CBox class="network-ai-chat__provider-info">
                        <CBox class="network-ai-chat__provider-heading">
                          <OmtTypography as="h3-bold" class="network-ai-chat__provider-name">
                            {{ provider.name }}
                          </OmtTypography>
                          <OmtTypography
                            v-if="provider.specialties"
                            as="p2"
                            class="network-ai-chat__provider-specialties"
                          >
                            {{ provider.specialties }}
                          </OmtTypography>
                        </CBox>
                      </CBox>
                    </CFlex>

                    <CBox class="network-ai-chat__provider-details">
                      <CBox v-if="provider.network" class="network-ai-chat__provider-network">
                        {{ provider.network }}
                      </CBox>

                      <CFlex v-if="provider.address" class="network-ai-chat__provider-detail-list">
                        <CBox as="span" class="network-ai-chat__provider-detail">
                          <OmtIcons
                            class="network-ai-chat__provider-detail-icon"
                            name="map-marker-alt"
                            color="#111827"
                            size="inherit"
                          />
                          <span>{{ provider.address }}</span>
                        </CBox>
                      </CFlex>

                      <CFlex v-if="provider.phones.length" class="network-ai-chat__provider-detail-list">
                        <CBox
                          v-for="(phone, phoneIndex) in provider.phones"
                          :key="`${provider.id}-phone-${phoneIndex}`"
                          as="span"
                          class="network-ai-chat__provider-detail"
                        >
                          <OmtIcons
                            class="network-ai-chat__provider-detail-icon"
                            :name="provider.hasWhatsapp ? 'whatsapp' : 'phone-alt'"
                            :color="provider.hasWhatsapp ? '#008A45' : '#111827'"
                            size="inherit"
                          />
                          {{ phone }}
                        </CBox>
                      </CFlex>

                      <CFlex v-if="provider.languages" class="network-ai-chat__provider-detail">
                        <OmtIcons
                          class="network-ai-chat__provider-detail-icon"
                          name="microphone"
                          prefix="fas"
                          color="#111827"
                          size="inherit"
                        />
                        <span>{{ provider.languages }}</span>
                      </CFlex>

                      <CFlex v-if="provider.teleconsultation" class="network-ai-chat__provider-detail">
                        <OmtIcons
                          class="network-ai-chat__provider-detail-icon"
                          name="video"
                          color="#111827"
                          size="inherit"
                        />
                        <span>Teleconsulta</span>
                      </CFlex>
                    </CBox>
                  </CBox>

                  <CFlex class="network-ai-chat__provider-actions">
                    <CBox
                      v-if="provider.phoneOptions.length === 1"
                      as="a"
                      :href="provider.phoneOptions[0].link"
                      class="network-ai-chat__provider-action"
                      title="Telefone"
                    >
                      <OmtIcons name="phone-alt" color="#0047A1" size="inherit" font-size="18px" />
                    </CBox>
                    <CBox
                      v-else-if="provider.phoneOptions.length > 1"
                      as="button"
                      type="button"
                      class="network-ai-chat__provider-action"
                      aria-haspopup="menu"
                      title="Telefone"
                      @click.stop.prevent="openProviderPhones(provider)"
                    >
                      <OmtIcons name="phone-alt" color="#0047A1" size="inherit" font-size="18px" />
                    </CBox>
                    <CBox v-else class="network-ai-chat__provider-action network-ai-chat__provider-action--disabled">
                      <OmtIcons name="phone-alt" color="#0047A1" size="inherit" font-size="18px" />
                    </CBox>

                    <CBox
                      v-if="provider.whatsappLink"
                      as="a"
                      :href="provider.whatsappLink"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="network-ai-chat__provider-action"
                      title="WhatsApp"
                    >
                      <OmtIcons name="whatsapp" color="#008A45" size="inherit" font-size="18px" />
                    </CBox>
                    <CBox
                      v-if="provider.mapLink && shouldShowIosMapOptions()"
                      as="button"
                      type="button"
                      class="network-ai-chat__provider-action"
                      title="Localização"
                      @click.stop.prevent="openMapOptions(provider)"
                    >
                      <OmtIcons name="location-dot" color="#0047A1" size="inherit" font-size="18px" />
                    </CBox>
                    <CBox
                      v-else-if="provider.mapLink"
                      as="a"
                      :href="getProviderMapLink(provider)"
                      :target="getProviderMapTarget()"
                      rel="noopener noreferrer"
                      class="network-ai-chat__provider-action"
                      title="Localização"
                    >
                      <OmtIcons name="location-dot" color="#0047A1" size="inherit" font-size="18px" />
                    </CBox>
                    <CBox v-else class="network-ai-chat__provider-action network-ai-chat__provider-action--disabled">
                      <OmtIcons name="location-dot" color="#0047A1" size="inherit" font-size="18px" />
                    </CBox>
                  </CFlex>
                </CBox>
              </CBox>

              <CBox v-else class="network-ai-chat__message-content">
                <CBox
                  class="network-ai-chat__bubble"
                  :class="message.from === 'user' ? 'network-ai-chat__bubble--user' : 'network-ai-chat__bubble--bot'"
                >
                  <PreviewFile
                    v-if="message.attachment"
                    :attachment="message.attachment"
                    :bg="message.from === 'user' ? '#E8F0FF' : '#ffffff'"
                    :maxW="['100%', '520px']"
                  />

                  <template v-else>
                    <OmtTypography
                      as="p1"
                      class="network-ai-chat__message-text"
                      :class="
                        message.from === 'user'
                          ? 'network-ai-chat__message-text--user'
                          : 'network-ai-chat__message-text--bot'
                      "
                    >
                      {{ message.text }}
                    </OmtTypography>
                    <OmtTypography
                      as="p2"
                      class="network-ai-chat__message-time"
                      :class="{ 'network-ai-chat__message-time--user': message.from === 'user' }"
                    >
                      {{ message.time }}
                      <CBox
                        v-if="message.from === 'user'"
                        as="span"
                        class="network-ai-chat__message-check"
                        aria-hidden="true"
                      >
                        ✓✓
                      </CBox>
                    </OmtTypography>
                  </template>
                </CBox>
                <CFlex v-if="message.options && message.options.length" class="network-ai-chat__quick-replies">
                  <CBox
                    v-for="option in message.options"
                    :key="`${message.id}-${option.value}`"
                    as="button"
                    type="button"
                    class="network-ai-chat__quick-reply"
                    :disabled="isLoading"
                    @click="sendQuickReply(option.value)"
                  >
                    {{ option.label }}
                  </CBox>
                </CFlex>
              </CBox>
            </CFlex>

            <CFlex v-if="isLoading" class="network-ai-chat__loading-row">
              <CBox class="network-ai-chat__loading-bubble" role="status" aria-live="polite">
                <OmtTypography as="p1" class="network-ai-chat__loading-text">
                  Aguarde por favor
                  <span class="loading-dots" aria-hidden="true">
                    <span class="loading-dot loading-dot--1"></span>
                    <span class="loading-dot loading-dot--2"></span>
                    <span class="loading-dot loading-dot--3"></span>
                  </span>
                </OmtTypography>
              </CBox>
            </CFlex>
          </CBox>
        </CBox>

        <transition name="network-ai-chat__scroll-button-transition">
          <CBox
            v-if="showScrollToBottom && (haveMessages || isLoading)"
            as="button"
            type="button"
            class="network-ai-chat__scroll-button"
            title="Ir para a mensagem mais recente"
            aria-label="Ir para a mensagem mais recente"
            @click="scrollToBottom"
          >
            <OmtIcons name="chevron-down" color="#0047A1" size="sm" />
          </CBox>
        </transition>
      </CBox>

      <CBox v-if="haveMessages || isLoading" class="network-ai-chat__footer">
        <PreviewFile
          v-if="selectedFile"
          wrap
          mb="12px"
          :file="selectedFile"
          removable
          bg="#F8FAFC"
          border="1px solid #E2E8F0"
          @remove="removeSelectedFile"
        />

        <CFlex class="network-ai-chat__composer">
          <OmtTextarea
            ref="textarea"
            class="network-ai-chat__textarea"
            :initialValue="observation"
            placeholder="Digite sua mensagem..."
            @change="handleChange"
            @keydown.native="handleTextareaKeydown"
          />

          <CFlex class="network-ai-chat__actions">
            <input ref="fileInput" type="file" hidden @change="handleFileSelected" />

            <OmtButton
              v-if="!hasDraft && !isRecording"
              class="network-ai-chat__icon-button"
              type="button"
              title="Usar voz"
              @click="startRecording"
            >
              <OmtIcons name="microphone" color="#0047A1" />
            </OmtButton>

            <OmtButton
              v-else-if="isRecording"
              class="network-ai-chat__icon-button"
              type="button"
              title="Parar gravação"
              @click="stopRecording"
            >
              <OmtIcons name="xmark" color="#0047A1" />
            </OmtButton>

            <OmtButton
              v-else
              class="network-ai-chat__icon-button"
              type="button"
              title="Enviar"
              :isDisabled="isLoading"
              @click="handleSend"
            >
              <OmtIcons name="paper-plane-top" color="#0047A1" />
            </OmtButton>
          </CFlex>
        </CFlex>
      </CBox>

      <transition name="network-ai-chat__phone-sheet-transition">
        <CBox
          v-if="selectedPhoneProvider"
          class="network-ai-chat__phone-sheet-backdrop"
          role="presentation"
          @click="closePhoneOptions"
        >
          <CBox class="network-ai-chat__phone-sheet" role="menu" aria-label="Escolha um número" @click.stop>
            <CBox class="network-ai-chat__phone-sheet-handle" aria-hidden="true" />
            <OmtTypography as="h3-bold" class="network-ai-chat__phone-sheet-title"> Escolha um número </OmtTypography>

            <CBox
              v-for="phone in selectedPhoneProvider.phoneOptions"
              :key="`${selectedPhoneProvider.id}-phone-sheet-${phone.label}`"
              as="a"
              :href="phone.link"
              role="menuitem"
              class="network-ai-chat__phone-sheet-option"
              @click="closePhoneOptions"
            >
              <CFlex alignItems="center">
                <CFlex class="network-ai-chat__phone-sheet-icon">
                  <OmtIcons name="phone-alt" color="#0047A1" size="inherit" font-size="16px" />
                </CFlex>
                <CBox>
                  <OmtTypography as="h3-semi-bold" class="network-ai-chat__phone-sheet-number">
                    {{ phone.label }}
                  </OmtTypography>
                  <OmtTypography as="p2" class="network-ai-chat__phone-sheet-caption">Telefone</OmtTypography>
                </CBox>
              </CFlex>
            </CBox>
          </CBox>
        </CBox>
      </transition>

      <transition name="network-ai-chat__ios-action-sheet-transition">
        <CBox
          v-if="selectedMapProvider"
          class="network-ai-chat__ios-action-sheet-backdrop"
          role="presentation"
          @click="closeMapOptions"
        >
          <CBox
            class="network-ai-chat__ios-action-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Abrir localização"
            @click.stop
          >
            <CBox class="network-ai-chat__ios-action-sheet-group">
              <CBox class="network-ai-chat__ios-action-sheet-header">
                <OmtTypography as="p2" class="network-ai-chat__ios-action-sheet-title">
                  Abrir localização
                </OmtTypography>
                <OmtTypography as="p2" class="network-ai-chat__ios-action-sheet-description">
                  Escolha um app para ver o endereço.
                </OmtTypography>
              </CBox>

              <CBox
                v-for="mapOption in getIosMapOptions(selectedMapProvider)"
                :key="`${selectedMapProvider.id}-map-sheet-${mapOption.label}`"
                as="a"
                :href="mapOption.url"
                class="network-ai-chat__ios-action-sheet-option"
                rel="noopener noreferrer"
                @click="closeMapOptions"
              >
                {{ mapOption.label }}
              </CBox>
            </CBox>

            <CBox as="button" type="button" class="network-ai-chat__ios-action-sheet-cancel" @click="closeMapOptions">
              Cancelar
            </CBox>
          </CBox>
        </CBox>
      </transition>
    </CFlex>
  </CBox>
</template>

<script>
import { CBox, CFlex } from "@chakra-ui/vue";
import { BuscarPrestadorViaChat } from "@/api/redeCredenciada";
import * as geoUtils from "@/utils/geoDistance";
import { getPlatform, PLATFORM } from "@/utils/platformUtil";
import PreviewFile from "./components/preview-file.vue";

function cleanProviderValue(value) {
  return `${value || ""}`.replace(/\s+/g, " ").trim();
}

function cleanMessageText(value) {
  return `${value || ""}`.replace(/\r\n?/g, "\n").trim();
}

async function getChatLocation() {
  if (typeof navigator === "undefined" || !navigator.geolocation) return null;

  const permission = await navigator.permissions?.query({ name: "geolocation" }).catch(() => null);
  if (permission?.state === "denied") return null;

  return geoUtils.GetLocation().catch(() => null);
}

function formatAddressWord(word) {
  const lower = word.toLowerCase();
  if (["da", "de", "do", "das", "dos"].includes(lower)) return lower;
  if (lower.length === 2) return lower.toUpperCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function formatAddressPart(value) {
  return cleanProviderValue(value).split(" ").filter(Boolean).map(formatAddressWord).join(" ");
}

function formatProviderAddress(record) {
  const rua = formatProviderList([record?.tp_logradouro, record?.en_rua], " ");
  const numero = cleanProviderValue(record?.en_numero);
  const complemento = formatAddressPart(record?.en_complemento);
  const bairro = formatAddressPart(record?.en_bairro);
  const cidade = formatAddressPart(record?.en_cidade);
  const estado = cleanProviderValue(record?.cd_estado).toUpperCase();
  const endereco = [rua, numero].filter(Boolean).join(", ");
  const cidadeEstado = [cidade, estado].filter(Boolean).join(" / ");

  return [endereco, complemento, bairro, cidadeEstado].filter(Boolean).join(" - ");
}

function formatProviderList(values, separator = " - ") {
  return values
    .map(cleanProviderValue)
    .filter(value => value && value !== "-")
    .join(separator);
}

function formatProviderPhones(value) {
  const values = Array.isArray(value) ? value : [value];

  return values.reduce((phones, item) => {
    cleanProviderValue(item)
      .split(/\s*(?:\/|;|\||,|\n)\s*/)
      .forEach(phone => {
        const cleanedPhone = cleanProviderValue(phone);
        if (cleanedPhone && !phones.includes(cleanedPhone)) phones.push(cleanedPhone);
      });

    return phones;
  }, []);
}

function buildPhoneLink(phone) {
  const digits = `${phone || ""}`.replace(/\D/g, "");
  return digits ? `tel:${digits}` : "";
}

function buildWhatsAppLink(phone) {
  const digits = `${phone || ""}`.replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) return "";

  return `https://api.whatsapp.com/send?phone=${digits.startsWith("55") ? digits : `55${digits}`}`;
}

function buildGoogleMapLink(latitude, longitude, address) {
  const query = latitude && longitude ? `${latitude},${longitude}` : cleanProviderValue(address);
  if (!query) return "";

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function buildMapAppLink(latitude, longitude, label, address) {
  if (!latitude || !longitude) {
    const query = cleanProviderValue(address);
    return query ? `geo:0,0?q=${encodeURIComponent(query)}` : "";
  }

  const coordinates = `${latitude},${longitude}`;
  const query = label ? `${coordinates}(${label})` : coordinates;
  return `geo:0,0?q=${encodeURIComponent(query)}`;
}

function buildAppleMapLink(latitude, longitude, label, address) {
  if (!latitude || !longitude) {
    const query = cleanProviderValue(address);
    return query ? `https://maps.apple.com/?q=${encodeURIComponent(query)}` : "";
  }

  const coordinates = `${latitude},${longitude}`;
  const query = encodeURIComponent(label || coordinates);
  return `https://maps.apple.com/?ll=${coordinates}&q=${query}`;
}

function buildWazeMapLink(latitude, longitude, address) {
  if (!latitude || !longitude) {
    const query = cleanProviderValue(address);
    return query ? `https://waze.com/ul?q=${encodeURIComponent(query)}` : "";
  }

  return `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
}

function isPositiveProviderValue(value) {
  return ["s", "sim", "true", "1", "yes"].includes(cleanProviderValue(value).toLowerCase());
}

function getSuggestedRepliesForMessage(message) {
  const text = cleanMessageText(message).toLowerCase();
  const hasMultipleChoiceOptions = /\b[a-f]\)/i.test(text);
  if (hasMultipleChoiceOptions) return [];

  const shouldShowMoreResults = text.includes("existem mais resultados");
  if (!shouldShowMoreResults) return [];

  return [
    { label: "Sim, ver mais prestadores", value: "Sim, quero ver mais prestadores" },
    { label: "Não", value: "Não" }
  ];
}

function hasNullProviderRecordset(response) {
  return response?.Data?.recordset === null;
}

function getProviderRecords(response) {
  return response?.Data?.recordset || [];
}

function getProviderReply(response) {
  return cleanMessageText(response?.Data?.reply || "");
}

function splitProviderReply(reply) {
  const text = cleanMessageText(reply);
  const intro = text.split(/(?:^|\n)\s*1\.\s*Nome\s*:/i)[0];
  const footer = text.match(/(?:Há|Ha) mais resultados[\s\S]*$/i)?.[0] || "";
  return {
    intro: intro.trim(),
    footer: footer.trim()
  };
}

function mapProviderRecord(record, index) {
  const phones = formatProviderPhones(record?.telefones);
  const phoneOptions = phones.map(phone => ({ label: phone, link: buildPhoneLink(phone) })).filter(phone => phone.link);
  const hasWhatsapp = cleanProviderValue(record?.cd_tipo_telefone).toUpperCase() === "WHT";
  const whatsappPhone = phones[0];
  const latitude = cleanProviderValue(record?.gm_lat);
  const longitude = cleanProviderValue(record?.gm_long);
  const name = cleanProviderValue(record?.ds_abreviado);
  const address = formatProviderAddress(record);
  const mapLink = buildGoogleMapLink(latitude, longitude, address);
  const mapAppLink = buildMapAppLink(latitude, longitude, name, address);
  const appleMapLink = buildAppleMapLink(latitude, longitude, name, address);
  const wazeMapLink = buildWazeMapLink(latitude, longitude, address);
  const code = cleanProviderValue(record?.cd_prestador);

  if (!name) return null;

  return {
    id: code || `${index}-${name}`,
    name,
    code,
    specialties: formatProviderList([record?.ds_especialidade, record?.ds_subespecialidade]),
    network: cleanProviderValue(record?.cd_rede),
    address,
    phones,
    phoneOptions,
    hasWhatsapp,
    whatsappLink: hasWhatsapp ? buildWhatsAppLink(whatsappPhone) : "",
    teleconsultation: isPositiveProviderValue(record?.possui_teleconferencia),
    languages: formatProviderList([record?.idiomas], ", "),
    mapLink,
    mapAppLink,
    appleMapLink,
    wazeMapLink
  };
}

export default {
  name: "Chat",
  components: {
    CBox,
    CFlex,
    PreviewFile
  },
  data() {
    return {
      omintLogoSrc: `${process.env.VUE_APP_BASE_URL}images/omintLogo.jpeg`,
      observation: "",
      messages: [],
      isLoading: false,
      selectedFile: null,
      isRecording: false,
      speechRecognition: null,
      selectedPhoneProvider: null,
      selectedMapProvider: null,
      showScrollToBottom: false,
      suggestions: [
        {
          title: "Cardiologista",
          icon: "heartbeat",
          text: "Quero um cardiologista próximo de mim"
        },
        {
          title: "Dermatologista",
          icon: "stethoscope",
          text: "Quero um dermatologista próximo de mim"
        },
        {
          title: "Pediatra",
          icon: "baby-carriage",
          text: "Quero um pediatra próximo de mim"
        },
        {
          title: "Hospital",
          icon: "hospital",
          text: "Quero um hospital próximo de mim"
        },
        {
          title: "Laboratório",
          icon: "flask",
          text: "Quero um laboratório próximo de mim"
        }
      ]
    };
  },
  computed: {
    haveMessages() {
      return this.messages.length > 0;
    },
    hasDraft() {
      return !!(this.observation && this.observation.trim()) || !!this.selectedFile;
    }
  },
  mounted() {
    this.$root.$emit("hide-loading");
    this.scrollToBottom();
  },
  beforeDestroy() {
    this.stopRecording();
    this.clearAttachmentMessagePreviewUrls();
  },
  methods: {
    shouldShowBotAvatar(index) {
      return index === 0 || this.messages[index - 1]?.from === "user";
    },
    getNowTime() {
      const date = new Date();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    },
    getMessagesContainer() {
      const ref = this.$refs.messagesContainer;
      return ref && (ref.$el || ref);
    },
    updateScrollButtonVisibility() {
      const el = this.getMessagesContainer();
      if (!el) {
        this.showScrollToBottom = false;
        return;
      }

      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      this.showScrollToBottom = distanceToBottom > 900;
    },
    handleMessagesScroll() {
      this.updateScrollButtonVisibility();
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.getMessagesContainer();
        if (!el) return;

        if (typeof el.scrollTo === "function") {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        } else {
          el.scrollTop = el.scrollHeight;
        }

        this.showScrollToBottom = false;
      });
    },
    preserveScrollPosition() {
      this.$nextTick(() => {
        this.updateScrollButtonVisibility();
      });
    },
    clearAttachmentMessagePreviewUrls() {
      (this.messages || []).forEach(message => {
        const url = message?.attachment?.previewUrl;
        if (url) URL.revokeObjectURL(url);
      });
    },
    getFileExtensionFromName(name) {
      const fileName = (name || "").trim();
      const extensionIndex = fileName.lastIndexOf(".");
      if (extensionIndex === -1) return "ARQ";
      const extension = fileName.slice(extensionIndex + 1).trim();
      return (extension || "ARQ").slice(0, 6).toUpperCase();
    },
    setSelectedFile(file) {
      this.selectedFile = file || null;
    },
    removeSelectedFile() {
      this.setSelectedFile(null);
      const input = this.$refs.fileInput;
      const el = input && (input.$el || input);
      if (el) el.value = "";
    },

    openProviderPhones(provider) {
      this.selectedPhoneProvider = provider;
    },
    closePhoneOptions() {
      this.selectedPhoneProvider = null;
    },
    shouldShowIosMapOptions() {
      return getPlatform() === PLATFORM.IOS;
    },
    getProviderMapLink(provider) {
      if (!provider) return "";

      if (getPlatform() === PLATFORM.ANDROID) return provider.mapAppLink || provider.mapLink;

      return provider.mapLink;
    },
    getProviderMapTarget() {
      return getPlatform() === PLATFORM.DESKTOP ? "_blank" : null;
    },
    closeMapOptions() {
      this.selectedMapProvider = null;
    },
    getIosMapOptions(provider) {
      if (!provider) return [];

      return [
        { label: "Apple Maps", url: provider.appleMapLink },
        { label: "Google Maps", url: provider.mapLink },
        { label: "Waze", url: provider.wazeMapLink }
      ].filter(option => option.url);
    },
    openMapOptions(provider) {
      this.selectedMapProvider = provider;
    },
    pushBotMessage(text, options = {}) {
      const message = cleanMessageText(text);
      if (!message) return;
      const messageOptions = Array.isArray(options)
        ? options
        : options.withQuickReplies
        ? getSuggestedRepliesForMessage(message)
        : [];

      this.messages.push({
        id: `m_${Date.now()}_${Math.random().toString(16).slice(2)}_bot`,
        from: "system",
        text: message,
        time: this.getNowTime(),
        options: messageOptions
      });
    },
    pushProviderCards(records) {
      const providers = records.map(mapProviderRecord).filter(Boolean);
      if (!providers.length) return false;

      this.messages.push({
        id: `m_${Date.now()}_${Math.random().toString(16).slice(2)}_providers`,
        from: "system",
        text: "",
        time: this.getNowTime(),
        providers
      });

      return true;
    },
    async fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async handleSend() {
      if (!this.observation && !this.selectedFile) return;

      const sendingFile = this.selectedFile;
      const now = Date.now();
      const time = this.getNowTime();
      const messageText = this.observation;

      this.isLoading = true;
      this.closePhoneOptions();

      if (sendingFile) {
        const file = sendingFile;
        const ext = this.getFileExtensionFromName(file.name).toLowerCase();
        const isImage =
          (!!file.type && file.type.startsWith("image/")) ||
          ["png", "jpg", "jpeg", "webp", "gif", "bmp", "heic", "heif"].includes(ext);
        const previewUrl = isImage ? URL.createObjectURL(file) : "";
        this.messages.push({
          id: `m_${now}_file`,
          from: "user",
          text: "",
          time,
          attachment: {
            name: file.name,
            type: file.type || "",
            extension: this.getFileExtensionFromName(file.name),
            previewUrl
          }
        });
      }

      if (messageText) {
        this.messages.push({
          id: `m_${now}`,
          from: "user",
          text: messageText,
          time
        });
      }

      this.observation = "";
      this.scrollToBottom();

      try {
        let imageDataUrl = null;
        if (sendingFile) {
          imageDataUrl = await this.fileToDataUrl(sendingFile);
        }
        const coords = await getChatLocation();

        const response = await BuscarPrestadorViaChat({
          message: messageText || "",
          imageDataUrl,
          latitude: coords?.latitude ?? null,
          longitude: coords?.longitude ?? null
        });

        this.isLoading = false;

        const reply = getProviderReply(response);
        const records = getProviderRecords(response);
        const shouldShowQuickReplies = hasNullProviderRecordset(response);
        const options = Array.isArray(response?.Data?.options) ? response.Data.options : [];
        const providerReply = records.length ? splitProviderReply(reply) : { intro: reply, footer: "" };
        let hasProviderCards = false;

        if (providerReply.intro) {
          this.pushBotMessage(
            providerReply.intro,
            options.length ? options : { withQuickReplies: shouldShowQuickReplies }
          );
        }
        if (records.length) hasProviderCards = this.pushProviderCards(records);
        if (providerReply.footer) this.pushBotMessage(providerReply.footer);
        if (!providerReply.intro && !hasProviderCards) {
          this.pushBotMessage(
            reply || "Não foi possível processar sua mensagem no momento. Tente novamente em instantes."
          );
        }

        if (hasProviderCards) {
          this.preserveScrollPosition();
        } else {
          this.scrollToBottom();
        }
      } catch (error) {
        console.error(error);
        this.isLoading = false;
        this.pushBotMessage("Ocorreu um erro ao enviar sua mensagem. Verifique sua conexão e tente novamente.");
        this.scrollToBottom();
      } finally {
        this.removeSelectedFile();
      }
    },
    handleChange(text) {
      this.observation = text;
    },
    handleTextareaKeydown(event) {
      if (event.key === "Enter") {
        if (event.ctrlKey) {
          return;
        } else {
          event.preventDefault();
          this.handleSend();
        }
      }
    },
    applySuggestion(text) {
      this.observation = text;
    },

    sendQuickReply(text) {
      if (this.isLoading) return;
      this.observation = text;
      this.$nextTick(() => this.handleSend());
    },
    handleAttachClick() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    handleFileSelected(event) {
      const selectedFile = event && event.target && event.target.files ? event.target.files[0] : null;
      if (selectedFile) this.setSelectedFile(selectedFile);

      if (event && event.target) event.target.value = null;
    },
    startRecording() {
      try {
        const SpeechRecognition = globalThis.window.SpeechRecognition || globalThis.window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
          alert("Seu navegador não suporta reconhecimento de voz.");
          return;
        }

        if (this.isRecording) return;

        this.speechRecognition = new SpeechRecognition();
        this.speechRecognition.lang = "pt-BR";
        this.speechRecognition.interimResults = true;
        this.speechRecognition.maxAlternatives = 1;
        this.isRecording = true;

        let finalTranscript = this.observation || "";

        this.speechRecognition.onresult = event => {
          let interimTranscript = "";
          for (let resultIndex = event.resultIndex; resultIndex < event.results.length; resultIndex++) {
            const transcript = event.results[resultIndex][0].transcript;
            if (event.results[resultIndex].isFinal) {
              finalTranscript += (finalTranscript ? " " : "") + transcript;
            } else {
              interimTranscript += transcript;
            }
          }
          this.observation = finalTranscript + (interimTranscript ? ` ${interimTranscript}` : "");
        };

        this.speechRecognition.onerror = () => {
          this.isRecording = false;
        };

        this.speechRecognition.onend = () => {
          this.isRecording = false;
          this.speechRecognition = null;
        };

        this.speechRecognition.start();
      } catch (err) {
        console.error(err);
        alert(err.message || "Falha ao iniciar reconhecimento de voz.");
        this.isRecording = false;
      }
    },
    stopRecording() {
      try {
        if (this.speechRecognition) {
          this.speechRecognition.stop();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isRecording = false;
        this.speechRecognition = null;
      }
    }
  }
};
</script>

<style scoped>
.network-ai-chat {
  width: 100%;
  min-height: calc(100vh - 72px);
  min-height: calc(100dvh - 72px);
  padding: 0;
  color: #111827;
  background: #ffffff;
}

.network-ai-chat__panel {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: calc(100vh - 72px);
  height: calc(100dvh - 72px);
  min-height: calc(100vh - 72px);
  min-height: calc(100dvh - 72px);
  margin: 0 auto;
  overflow: hidden;
  background: #ffffff;
}

.network-ai-chat__scroll-area {
  position: relative;
  flex: 1;
  min-height: 0;
}

.network-ai-chat__body {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 48px 32px 16px;
}

.network-ai-chat__body--conversation {
  padding: 32px 16px 16px;
}

.network-ai-chat__scroll-button {
  position: absolute;
  right: 16px;
  bottom: 16px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid #dbe4f0;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(16, 24, 40, 0.18);
  cursor: pointer;
}

.network-ai-chat__scroll-button:active {
  transform: translateY(1px);
}

.network-ai-chat__scroll-button-transition-enter-active,
.network-ai-chat__scroll-button-transition-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}

.network-ai-chat__scroll-button-transition-enter,
.network-ai-chat__scroll-button-transition-leave-to {
  transform: translateY(8px);
  opacity: 0;
}

.network-ai-chat__home {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: calc(100vh - 252px);
  text-align: center;
}

.network-ai-chat__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin: 24px 0 20px;
  overflow: hidden;
}

.network-ai-chat__logo-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.network-ai-chat__title {
  margin: 0 0 8px;
  color: #111827;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.network-ai-chat__subtitle {
  max-width: 260px;
  margin: 0 auto;
  color: #4a5e77;
  font-size: 14px;
  line-height: 20px;
}

.network-ai-chat__composer {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 0 8px 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #ffffff;
}

.network-ai-chat__composer--home {
  min-height: 60px;
  margin-top: 40px;
  padding: 10px 12px;
  color: #4a5e77;
  text-align: left;
}

.network-ai-chat__textarea {
  flex: 1;
  height: 48px;
  min-height: 48px;
  max-height: 96px;
  padding: 12px 8px;
  resize: none;
  border: 0;
  border-radius: 12px;
  background: transparent;
  box-shadow: none;
  font-size: 14px;
  line-height: 20px;
}

.network-ai-chat__textarea:focus {
  border-color: transparent;
  box-shadow: none;
}

@media (max-width: 430px) {
  .network-ai-chat__composer--home {
    min-height: 72px;
  }

  .network-ai-chat__composer--home .network-ai-chat__textarea {
    height: 52px;
    min-height: 52px;
    padding-top: 6px;
    padding-bottom: 6px;
  }
}

.network-ai-chat__icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
}

.network-ai-chat__suggestions {
  width: 100%;
  max-width: 100%;
  margin-top: 40px;
  text-align: left;
}

.network-ai-chat__suggestions-title {
  display: block;
  margin: 0 0 12px;
  color: #111827;
  font-size: 12px;
  font-weight: 700;
}

.network-ai-chat__suggestions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.network-ai-chat__suggestion {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 8px 12px;
  border: 1px solid #edf1f7;
  border-radius: 12px;
  color: #0047a1;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;
}

.network-ai-chat__suggestion:active {
  transform: translateY(1px);
}

.network-ai-chat__suggestion-icon {
  display: inline-flex;
  align-items: center;
  min-width: 14px;
  margin-right: 8px;
}

.network-ai-chat__messages {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding-bottom: 16px;
}

.network-ai-chat__message-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  padding-right: 40px;
}

.network-ai-chat__message-row--user {
  align-items: flex-end;
  justify-content: flex-end;
  padding-right: 0;
  padding-left: 64px;
}

.network-ai-chat__message-row--providers {
  padding-right: 0;
}

.network-ai-chat__message-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
}

.network-ai-chat__message-row--user .network-ai-chat__message-content {
  align-items: flex-end;
}

.network-ai-chat__avatar,
.network-ai-chat__avatar-spacer {
  width: 32px;
  min-width: 32px;
  height: 32px;
  margin-right: 8px;
}

.network-ai-chat__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.network-ai-chat__message-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
}

.network-ai-chat__message-row--user .network-ai-chat__message-content {
  align-items: flex-end;
}

.network-ai-chat__bubble {
  max-width: 100%;
  padding: 12px;
  border-radius: 12px;
  word-break: break-word;
  text-align: left;
}

.network-ai-chat__bubble--bot {
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
}

.network-ai-chat__bubble--user {
  border: 0;
  background: #e8f0ff;
}

.network-ai-chat__providers {
  display: grid;
  width: min(100%, 680px);
  gap: 12px;
}

.network-ai-chat__provider-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
  text-align: left;
}

.network-ai-chat__provider-content {
  padding: 16px 16px 0;
}

.network-ai-chat__provider-main {
  align-items: center;
}

.network-ai-chat__provider-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  min-width: 56px;
  height: 56px;
  margin-right: 12px;
  border-radius: 50%;
  background: #f3f4f6;
}

.network-ai-chat__provider-info {
  flex: 1;
  min-width: 0;
}

.network-ai-chat__provider-heading {
  min-width: 0;
}

.network-ai-chat__provider-name {
  display: -webkit-box;
  margin: 0 0 2px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #111827;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}

.network-ai-chat__provider-specialties {
  margin: 0;
  color: #111827;
  font-size: 12px;
  line-height: 16px;
}

.network-ai-chat__provider-network {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  margin-top: 0;
  padding: 3px 8px;
  border-radius: 8px;
  color: #0047a1;
  background: #e8f0ff;
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
}

.network-ai-chat__provider-details {
  margin-top: 8px;
}

.network-ai-chat__provider-detail,
.network-ai-chat__provider-detail-list {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  color: #111827;
  font-size: 12px;
  line-height: 18px;
}

.network-ai-chat__provider-detail-list {
  flex-wrap: wrap;
  column-gap: 12px;
}

.network-ai-chat__provider-detail-list .network-ai-chat__provider-detail {
  margin-top: 0;
}

.network-ai-chat__provider-detail-icon {
  width: 16px;
  min-width: 16px;
  height: 16px;
  flex-shrink: 0;
  font-size: 16px;
  line-height: 16px;
}

.network-ai-chat__provider-detail-icon svg {
  width: 16px;
  height: 16px;
}

.network-ai-chat__provider-actions {
  align-items: center;
  height: 44px;
  margin-top: 12px;
  border-top: 1px solid #e5e7eb;
  background: #f7f9fc;
}

.network-ai-chat__provider-action {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-width: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-decoration: none;
}

.network-ai-chat__provider-action + .network-ai-chat__provider-action {
  border-left: 1px solid #e5e7eb;
}

.network-ai-chat__provider-action--disabled {
  opacity: 0.45;
  pointer-events: none;
}

.network-ai-chat__message-text {
  margin: 0;
  white-space: pre-wrap;
}

.network-ai-chat__message-text--bot {
  color: #111827;
}

.network-ai-chat__message-text--user {
  color: #0047a1;
}

.network-ai-chat__message-time {
  margin: 8px 0 0;
  color: #4a5e77;
  font-size: 11px;
  line-height: 14px;
}

.network-ai-chat__message-time--user {
  text-align: right;
}

.network-ai-chat__message-check {
  margin-left: 4px;
  color: #1e8ee5;
  font-size: 10px;
}

.network-ai-chat__quick-replies {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.network-ai-chat__quick-reply {
  min-height: 38px;
  padding: 8px 16px;
  border: 1px solid #d7e3f8;
  border-radius: 999px;
  color: #0047a1;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 71, 161, 0.12);
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  cursor: pointer;
}

.network-ai-chat__quick-reply:disabled {
  cursor: default;
  opacity: 0.55;
}

.network-ai-chat__phone-sheet-backdrop {
  position: fixed;
  top: 72px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
  background: rgba(17, 24, 39, 0.3);
}

.network-ai-chat__phone-sheet-transition-enter-active,
.network-ai-chat__phone-sheet-transition-leave-active {
  transition: opacity 180ms ease;
}

.network-ai-chat__phone-sheet-transition-enter,
.network-ai-chat__phone-sheet-transition-leave-to {
  opacity: 0;
}

.network-ai-chat__phone-sheet-transition-enter-active .network-ai-chat__phone-sheet,
.network-ai-chat__phone-sheet-transition-leave-active .network-ai-chat__phone-sheet {
  transition: transform 220ms ease, opacity 180ms ease;
}

.network-ai-chat__phone-sheet-transition-enter .network-ai-chat__phone-sheet,
.network-ai-chat__phone-sheet-transition-leave-to .network-ai-chat__phone-sheet {
  opacity: 0;
  transform: translateY(28px);
}

.network-ai-chat__phone-sheet {
  width: 100%;
  max-width: 360px;
  padding: 8px 0;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 12px 36px rgba(17, 24, 39, 0.18);
}

.network-ai-chat__phone-sheet-handle {
  width: 36px;
  height: 4px;
  margin: 4px auto 12px;
  border-radius: 999px;
  background: #d1d5db;
}

.network-ai-chat__phone-sheet-title {
  display: block;
  margin: 0;
  padding: 0 20px 8px;
  color: #111827;
}

.network-ai-chat__phone-sheet-option {
  display: block;
  padding: 12px 20px;
  color: #111827;
  text-decoration: none;
}

.network-ai-chat__phone-sheet-option + .network-ai-chat__phone-sheet-option {
  border-top: 1px solid #edf1f7;
}

.network-ai-chat__phone-sheet-icon {
  align-items: center;
  justify-content: center;
  width: 36px;
  min-width: 36px;
  height: 36px;
  margin-right: 12px;
  border-radius: 50%;
  background: #e8f0ff;
}

.network-ai-chat__phone-sheet-number {
  display: block;
  color: #111827;
}

.network-ai-chat__phone-sheet-caption {
  display: block;
  margin-top: 2px;
  color: #64748b;
}

.network-ai-chat__ios-action-sheet-backdrop {
  position: fixed;
  top: 72px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 21;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 8px 8px calc(16px + env(safe-area-inset-bottom));
  background: rgba(17, 24, 39, 0.36);
}

.network-ai-chat__ios-action-sheet-transition-enter-active,
.network-ai-chat__ios-action-sheet-transition-leave-active {
  transition: opacity 180ms ease;
}

.network-ai-chat__ios-action-sheet-transition-enter,
.network-ai-chat__ios-action-sheet-transition-leave-to {
  opacity: 0;
}

.network-ai-chat__ios-action-sheet-transition-enter-active .network-ai-chat__ios-action-sheet,
.network-ai-chat__ios-action-sheet-transition-leave-active .network-ai-chat__ios-action-sheet {
  transition: transform 220ms ease, opacity 180ms ease;
}

.network-ai-chat__ios-action-sheet-transition-enter .network-ai-chat__ios-action-sheet,
.network-ai-chat__ios-action-sheet-transition-leave-to .network-ai-chat__ios-action-sheet {
  opacity: 0;
  transform: translateY(24px);
}

.network-ai-chat__ios-action-sheet {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 390px;
}

.network-ai-chat__ios-action-sheet-group,
.network-ai-chat__ios-action-sheet-cancel {
  overflow: hidden;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 12px 36px rgba(17, 24, 39, 0.18);
}

.network-ai-chat__ios-action-sheet-header {
  padding: 12px 20px 10px;
  border-bottom: 1px solid rgba(60, 60, 67, 0.18);
  text-align: center;
}

.network-ai-chat__ios-action-sheet-title {
  display: block;
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}

.network-ai-chat__ios-action-sheet-description {
  display: block;
  margin-top: 2px;
  color: #8a94a6;
  font-size: 12px;
  line-height: 16px;
}

.network-ai-chat__ios-action-sheet-option,
.network-ai-chat__ios-action-sheet-cancel {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 56px;
  padding: 12px 20px;
  border: 0;
  color: #0047a1;
  background: transparent;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  text-align: center;
  text-decoration: none;
}

.network-ai-chat__ios-action-sheet-option + .network-ai-chat__ios-action-sheet-option {
  border-top: 1px solid rgba(60, 60, 67, 0.18);
}

.network-ai-chat__ios-action-sheet-cancel {
  margin-top: 8px;
  color: #0047a1;
  background: #ffffff;
  font-weight: 700;
  cursor: pointer;
}

.network-ai-chat__quick-replies {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.network-ai-chat__quick-reply {
  min-height: 36px;
  padding: 8px 12px;
  border: 1px solid #edf1f7;
  border-radius: 12px;
  color: #0047a1;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 71, 161, 0.12);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;
}

.network-ai-chat__quick-reply:disabled {
  cursor: default;
  opacity: 0.55;
}

.network-ai-chat__loading-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
}

.network-ai-chat__loading-bubble {
  min-height: 36px;
  margin-top: 28px;
  padding: 10px 12px;
  border-radius: 18px;
  color: #111827;
  background: #f3f4f6;
}

.network-ai-chat__loading-text {
  margin: 0;
}

.network-ai-chat__footer {
  flex-shrink: 0;
  padding: 10px 12px calc(12px + env(safe-area-inset-bottom));
  background: #f7f9fc;
  border-top: 1px solid #edf1f7;
}

.network-ai-chat__actions {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.loading-dots {
  display: inline-flex;
  align-items: flex-end;
  gap: 4px;
  margin-left: 12px;
  vertical-align: middle;
}

.loading-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  animation: loading-dot-bounce 900ms ease-in-out infinite;
}

.loading-dot--1 {
  background: #64748b;
  animation-delay: 0ms;
}

.loading-dot--2 {
  background: #475569;
  animation-delay: 120ms;
}

.loading-dot--3 {
  background: #334155;
  animation-delay: 240ms;
}

@media (min-width: 768px) {
  .network-ai-chat {
    padding: 24px 0;
    background: #f7f9fc;
  }

  .network-ai-chat__panel {
    width: calc(100% - 48px);
    max-width: 640px;
    height: calc(100vh - 128px);
    height: calc(100dvh - 128px);
    min-height: calc(100vh - 128px);
    min-height: calc(100dvh - 128px);
    border: 1px solid #edf1f7;
    border-radius: 16px;
    box-shadow: 0 18px 48px rgba(16, 24, 40, 0.08);
  }

  .network-ai-chat__body--conversation {
    padding-right: 24px;
    padding-left: 24px;
  }

  .network-ai-chat__home {
    min-height: calc(100vh - 336px);
  }

  .network-ai-chat__message-text {
    font-size: 16px;
    line-height: 22px;
  }

  .network-ai-chat__footer {
    padding: 12px 32px calc(24px + env(safe-area-inset-bottom));
  }
}

@media (min-width: 1200px) {
  .network-ai-chat {
    padding-right: 80px;
    padding-left: 80px;
  }

  .network-ai-chat__panel {
    max-width: 1104px;
  }

  .network-ai-chat__body--conversation {
    padding-right: 48px;
    padding-left: 48px;
  }

  .network-ai-chat__footer {
    padding-right: 48px;
    padding-left: 48px;
  }
}

@keyframes loading-dot-bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.7;
  }

  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}
</style>
